import * as React from 'react';
// Import props.
import {
	ComparativeDataHeaderProps,
	ComparativeDataRowProps,
	ComparativePublicBuildingSummaryProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
} from '@app/enumerations';
// Import services.
import {
	AddressService,
	StringService,
} from '@app/services';
// Import interfaces 
import {
	IAddress,
} from '@app/interfaces';
interface UseComparativePublicBuildingSummaryParameters {
	props: ComparativePublicBuildingSummaryProps;
}

interface UseComparativePublicBuildingSummaryResults {
	applyBuildingComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyBuildingAddressComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyBuildingDetailsComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyBuildingLimitsComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyBuildingDetailsComparativeDataRowProps: () => ComparativeDataRowProps[];
	applyBuildingLimitsComparativeDataRowProps: () => ComparativeDataRowProps[];
	buildingBaseId: string;
	buildingAddressBaseId: string;
	buildingDetailsBaseId: string;
	buildingLimitsBaseId: string;
	showAddressSubheader: boolean;
}

const useComparativePublicBuildingSummary = (params: UseComparativePublicBuildingSummaryParameters): UseComparativePublicBuildingSummaryResults => {

	// #region exported methods

	/**
	 * Returns the building comparative data header props. 
	 **/
	const applyBuildingComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${buildingBaseId}-header`,
			isSticky: areHeadersSticky,
			title: params.props.title.toTitleCase(),
			titlePostFixFontSize: 'xsm',
			titleFontSize: 'sm',
		};
	};

	/**
	 * Returns the building address comparative data header props. 
	 **/
	const applyBuildingAddressComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${buildingAddressBaseId}-header`,
			isSticky: areHeadersSticky,
			title: singleLineAddress,
			titleColor: AppThemeColor.INFO,
			titleFontSize: 'xxsm',
			titleFontStyle: 'italic',
			titleFontWeight: 'normal',
			paddingProps: {
				bottom: 1,
				top: 1,
			},
		};
	};


	/**
	 * Returns building details comparative data header props. 
	 **/
	const applyBuildingDetailsComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${buildingDetailsBaseId}-header`,
			isSticky: false,
			title: 'Building Information',
			titleFontSize: 'xxsm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns building limits comparative data header props. 
	 **/
	const applyBuildingLimitsComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${buildingLimitsBaseId}-header`,
			isSticky: false,
			title: 'Coverage Limits',
			titleFontSize: 'xxsm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns building details comparative data row props. 
	 **/
	const applyBuildingDetailsComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return [
			{
				fieldLabel: 'Flood Zone',
				oldValue: designatedFloodZone,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
		];
	};

	/**
	 * Returns building limits comparative data row props. 
	 **/
	const applyBuildingLimitsComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return [
			{
				fieldLabel: 'Building',
				oldValue: buildingLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Contents',
				oldValue: contentsLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Business Income and Extra Expense',
				oldValue: bieeLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Total Primary Limit',
				oldValue: totalPrimaryLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
		];
	};

	// #endregion exported methods


	// #region private methods

	// #endregion private methods


	// #region variables

	const areHeadersSticky: boolean = React.useMemo(() => {
		return params.props.areHeadersSticky ?? false;
	}, [params.props.areHeadersSticky]);

	const buildingBaseId: string = React.useMemo(() => {
		return `${params.props.id}-building`;
	}, [params.props.id]);

	const buildingAddressBaseId: string = React.useMemo(() => {
		return `${buildingBaseId}-address`;
	}, [buildingBaseId]);

	const buildingDetailsBaseId: string = React.useMemo(() => {
		return `${buildingBaseId}-details`;
	}, [buildingBaseId]);

	const buildingLimitsBaseId: string = React.useMemo(() => {
		return `${buildingBaseId}-limits`;
	}, [buildingBaseId]);

	// Building details.
	const designatedFloodZone: string | undefined = React.useMemo(() => {
		return params.props.building?.buildingData?.floodZone ?? 'Unspecified';
	}, [params.props.building?.buildingData?.floodZone]);

	// Limits
	const buildingLimit: number = React.useMemo(() => params.props.building?.limits?.building ?? 0, [params.props.building?.limits?.building]);
	const contentsLimit: number = React.useMemo(() => params.props.building?.limits?.contents ?? 0, [params.props.building?.limits?.contents]);
	const bieeLimit: number = React.useMemo(() => params.props.building?.limits?.businessIncomeExtraExpense ?? 0, [params.props.building?.limits?.businessIncomeExtraExpense]);

	const buildingLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(buildingLimit), [buildingLimit]);
	const contentsLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(contentsLimit), [contentsLimit]);
	const bieeLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(bieeLimit), [bieeLimit]);

	const totalPrimaryLimit: number = React.useMemo(() => {
		return buildingLimit
			+ contentsLimit
			+ bieeLimit;
	}, [
		buildingLimit,
		contentsLimit,
		bieeLimitCurrencyLabel,
	]);
	const totalPrimaryLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(totalPrimaryLimit), [totalPrimaryLimit]);

	// Address
	const addressRecord: IAddress = React.useMemo(() => {
		return {
			addressLine1: params.props.building?.address?.address?.addressLine1 ?? String.empty(),
			addressLine2: params.props.building?.address?.address?.addressLine2 ?? String.empty(),
			city: params.props.building?.address?.address?.city ?? String.empty(),
			state: params.props.building?.address?.address?.state ?? String.empty(),
			postalCode: params.props.building?.address?.address?.postalCode ?? String.empty(),
		};
	}, [params.props.building?.address?.address]);
	const singleLineAddress: string = React.useMemo(() => AddressService.getInstance().formatAddressToSingleLineString(addressRecord), [addressRecord]);

	const showAddressSubheader: boolean = React.useMemo(() => {
		return !String.isNullOrWhiteSpace(singleLineAddress);
	}, [singleLineAddress]);

	// #endregion private variables

	// #region lifecycle methods.

	// #endregion lifecycle methods.

	return {
		applyBuildingComparativeDataHeaderProps,
		applyBuildingAddressComparativeDataHeaderProps,
		applyBuildingDetailsComparativeDataHeaderProps,
		applyBuildingLimitsComparativeDataHeaderProps,
		applyBuildingDetailsComparativeDataRowProps,
		applyBuildingLimitsComparativeDataRowProps,
		buildingBaseId,
		buildingAddressBaseId,
		buildingDetailsBaseId,
		buildingLimitsBaseId,
		showAddressSubheader,
	};
}

// Export parameters.
export {
	UseComparativePublicBuildingSummaryParameters,
}

// Export results.
export {
	UseComparativePublicBuildingSummaryResults,
}

export default useComparativePublicBuildingSummary;